import { formatCurrency } from '../../util';
import {
    CaretUpIcon,
    CaretDownIcon,
    InfoSignIcon,
    Tooltip,
    Pane
} from 'evergreen-ui';
import { useState, useEffect } from 'react';
import { Spinner, Alert } from 'evergreen-ui';
import { ForeignKeyMultiSelectDropDownInput } from '../../inputs/ForeignKeyInputs';
import { useSelector } from 'react-redux';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const styles = {
    page: {
        padding: 30,
        fontSize: 12,
    },
    header: {
        fontSize: 20,
        marginBottom: 20,
        textAlign: 'center',
    },
    subheader: {
        fontSize: 14,
        marginBottom: 10,
        marginTop: 20,
    },
    table: {
        width: '100%',
        borderCollapse: 'collapse',
        marginTop: '20px',
    },
    th: {
        padding: '12px',
        textAlign: 'left',
        backgroundColor: '#f5f6f7',
        borderBottom: '2px solid #ddd',
        cursor: 'pointer',
        position: 'relative',
    },
    td: {
        padding: '8px',
        borderBottom: '1px solid #ddd',
    },
    profit: {
        color: 'green',
    },
    loss: {
        color: 'red',
    },
    positive: {
        color: 'black',
    },
    negative: {
        color: 'red',
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#000',
        borderBottomStyle: 'solid',
        padding: 5,
    },
    tableHeader: {
        backgroundColor: '#f0f0f0',
    },
    tableCell: {
        flex: 1,
    },
    summary: {
        marginTop: 20,
        padding: 10,
        backgroundColor: '#f0f0f0',
    },
};

const ProfitLossReport = ({
    startDate,
    endDate,
    reportData,
    queryLoading,
    selectedProducts,
    handleProductChange,
    productsData,
    productsLoading,
    // ... other props
}) => {
    console.log('ProfitLossReport props:', {
        startDate,
        endDate,
        reportData,
        queryLoading,
        selectedProducts,
        productsData
    });

    // Add state for sorting
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: 'ascending'
    });

    // Add effect to handle report data changes
    useEffect(() => {
        console.log('reportData changed:', reportData);
        if (reportData) {
            console.log('Full report data:', reportData);
            console.log('Revenue by product array:', reportData.revenueByProduct);
            if (reportData.revenueByProduct?.length > 0) {
                console.log('First product details:', reportData.revenueByProduct[0]);
            }
        }
    }, [reportData]);

    // Get the publisher's currency from Redux store
    const publisherGlobalData = useSelector((state) => state.publisherGlobalData.value);
    const currencySymbol = publisherGlobalData?.currency?.symbol || '$';

    // Helper function to format currency with the publisher's currency symbol
    const formatCurrencyWithSymbol = (amount) => {
        return formatCurrency(amount, 'USD', currencySymbol);
    };

    // If we're loading or have no data, show appropriate UI
    if (queryLoading) return <Spinner />;
    if (!reportData) return <Alert intent="warning" title="No data available" />;

    // Add sorting function
    const handleSort = (key) => {
        let direction = 'ascending';
        if (sortConfig.key === key && sortConfig.direction === 'ascending') {
            direction = 'descending';
        }
        setSortConfig({ key, direction });
    };

    // Add function to get sorted data
    const getSortedData = (data) => {
        console.log('getSortedData input:', data);

        // Check if data is an array
        if (!Array.isArray(data)) {
            console.warn('Expected array for revenueByProduct, got:', data);
            return [];
        }

        if (!sortConfig.key) return data;

        return [...data].sort((a, b) => {
            if (a[sortConfig.key] === null) return 1;
            if (b[sortConfig.key] === null) return -1;

            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            // Handle numeric values
            if (['revenue', 'expenses', 'retailPrice', 'units'].includes(sortConfig.key)) {
                aVal = parseFloat(aVal) || 0;
                bVal = parseFloat(bVal) || 0;
            }

            if (aVal < bVal) {
                return sortConfig.direction === 'ascending' ? -1 : 1;
            }
            if (aVal > bVal) {
                return sortConfig.direction === 'ascending' ? 1 : -1;
            }
            return 0;
        });
    };

    // Add function to render sort indicator
    const renderSortIndicator = (key) => {
        if (sortConfig.key !== key) return null;
        return sortConfig.direction === 'ascending' ?
            <CaretUpIcon size={12} /> :
            <CaretDownIcon size={12} />;
    };

    // Add helper function to calculate monthly P&L
    const calculateMonthlyPL = (item) => {
        // Add debug logging
        console.log('Calculating monthly P&L for item:', {
            item,
            createdAt: item.createdAt || item.created_at
        });

        const creationDate = new Date(item.createdAt || item.created_at); // Handle both possible field names
        const now = new Date();

        // Add validation to ensure we have a valid date
        if (!creationDate || isNaN(creationDate.getTime())) {
            console.warn('Invalid creation date for product:', item);
            return 0;
        }

        const monthsDiff = (now.getFullYear() - creationDate.getFullYear()) * 12 +
                          (now.getMonth() - creationDate.getMonth());
        const totalMonths = Math.max(1, monthsDiff); // Ensure we don't divide by zero
        const totalPL = (item.revenue || 0) - (item.expenses || 0);

        const monthlyPL = totalPL / totalMonths;
        console.log('Monthly P&L calculation:', {
            totalPL,
            totalMonths,
            monthlyPL
        });

        return monthlyPL;
    };

    return (
        <Pane>
            <Pane marginBottom={16}>
                <ForeignKeyMultiSelectDropDownInput
                    loading={productsLoading}
                    field="product"
                    values={productsData?.products?.map(product => {
                        return {
                            id: product.id.toString(),
                            key: product.id.toString(),
                            value: product.productWork?.title,
                            label: `${product.productWork?.title} (${product.format && product.format.description ? product.format.description : 'No format'})`,
                            description: product.isbn || '',
                            format: product.format
                        };
                    }) || []}
                    value={selectedProducts}
                    handleChange={handleProductChange}
                    el="product"
                    formLabel="Products"
                    placeholder="Select products..."
                />
            </Pane>

            {!reportData ? (
                <Alert intent="warning" title="No data available" />
            ) : (
                <div>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th} onClick={() => handleSort('title')}>
                                    Title {renderSortIndicator('title')}
                                </th>
                                <th style={styles.th} onClick={() => handleSort('isbn')}>
                                    ISBN {renderSortIndicator('isbn')}
                                </th>
                                <th style={styles.th} onClick={() => handleSort('formatType')}>
                                    Type {renderSortIndicator('formatType')}
                                </th>
                                <th style={styles.th} onClick={() => handleSort('retailPrice')}>
                                    Retail Price {renderSortIndicator('retailPrice')}
                                </th>
                                <th style={styles.th} onClick={() => handleSort('units')}>
                                    Quantity Sold {renderSortIndicator('units')}
                                </th>
                                <th style={styles.th} onClick={() => handleSort('revenue')}>
                                    Revenue {renderSortIndicator('revenue')}
                                </th>
                                <th style={styles.th} onClick={() => handleSort('expenses')}>
                                    Expenses {renderSortIndicator('expenses')}
                                </th>
                                <th style={styles.th} onClick={() => handleSort('profit')}>
                                    Profit in period {renderSortIndicator('profit')}
                                </th>
                                <th style={styles.th} onClick={() => handleSort('monthlyPL')}>
                                    Monthly P&L {renderSortIndicator('monthlyPL')}
                                    <Tooltip content="This is a calculation of the monthly P&L for the life of the product until now">
                                        <InfoSignIcon size={12} marginLeft={4} />
                                    </Tooltip>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {reportData.revenueByProduct ? (
                                getSortedData(reportData.revenueByProduct).map((item, index) => {
                                    console.log('Processing item:', item);
                                    const itemRevenue = item.revenue || 0;
                                    const itemExpenses = item.expenses || 0;
                                    const itemProfit = itemRevenue - itemExpenses;
                                    const monthlyPL = calculateMonthlyPL(item);

                                    // Add debug log for each row
                                    console.log('Rendering row:', {
                                        item,
                                        monthlyPL,
                                        profit: itemProfit
                                    });

                                    return (
                                        <tr key={index}>
                                            <td style={styles.td}>{item.title}</td>
                                            <td style={styles.td}>{item.isbn || 'N/A'}</td>
                                            <td style={styles.td}>{item.format_type || item.formatType || 'N/A'}</td>
                                            <td style={styles.td}>{formatCurrencyWithSymbol(item.retail_price || item.retailPrice || 0)}</td>
                                            <td style={styles.td}>{item.units || 0}</td>
                                            <td style={styles.td}>{formatCurrencyWithSymbol(itemRevenue)}</td>
                                            <td style={styles.td}>{formatCurrencyWithSymbol(itemExpenses)}</td>
                                            <td style={{...styles.td, color: itemProfit < 0 ? 'red' : 'green'}}>
                                                {formatCurrencyWithSymbol(itemProfit)}
                                            </td>
                                            <td style={{...styles.td, color: monthlyPL < 0 ? 'red' : 'green'}}>
                                                {formatCurrencyWithSymbol(monthlyPL)}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="9">No product data available</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </Pane>
    );
};

export const generateProfitLossPDF = (data, startDate, endDate, selectedProducts, currencySymbol = '$') => {
    // Helper function to format currency with the publisher's currency symbol
    const formatCurrencyWithSymbol = (amount) => {
        return formatCurrency(amount, 'USD', currencySymbol);
    };

    // Calculate total units by summing the units from each product
    const totalUnits = data.revenueByProduct ?
        data.revenueByProduct.reduce((sum, item) => sum + (parseInt(item.units) || 0), 0) : 0;

    // Create a hidden iframe
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    document.body.appendChild(iframe);

    const doc = iframe.contentWindow.document;
    doc.open();

    // Generate HTML content
    const content = `
        <!DOCTYPE html>
        <html>
        <head>
            <title>Profit & Loss by Product</title>
            <style>
                body { font-family: Arial, sans-serif; padding: 20px; }
                h1 { text-align: center; color: #234361; }
                table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                th, td { padding: 8px; text-align: left; border-bottom: 1px solid #ddd; }
                th { background-color: #f5f6f7; }
                .summary { background-color: #f5f6f7; padding: 15px; margin-top: 20px; }
                .total-row { font-weight: bold; background-color: #f5f6f7; }
                .profit { color: green; }
                .loss { color: red; }
                .book-title { color: #234361; }
            </style>
        </head>
        <body>
            <h1>Profit & Loss by Product</h1>
            <p>Period: ${startDate} to ${endDate}</p>

            <table>
                <thead>
                    <tr>
                        <th>Title</th>
                        <th>ISBN</th>
                        <th>Type</th>
                        <th>Retail Price</th>
                        <th>Quantity Sold</th>
                        <th>Revenue</th>
                        <th>Expenses</th>
                        <th>Profit in period</th>
                    </tr>
                </thead>
                <tbody>
                    ${data.revenueByProduct ? data.revenueByProduct.map(item => {
                        const title = item.title || 'Unknown Book';
                        const authors = item.authors && item.authors.length > 0
                            ? ` by ${item.authors.join(', ')}`
                            : '';
                        const itemRevenue = item.revenue || 0;
                        const itemExpenses = item.expenses || 0;
                        const itemProfit = itemRevenue - itemExpenses;
                        const profitClass = itemProfit >= 0 ? 'profit' : 'loss';

                        return `
                            <tr>
                                <td class="book-title">${title}${authors}</td>
                                <td>${item.isbn || 'N/A'}</td>
                                <td>${item.formatType || 'N/A'}</td>
                                <td>${formatCurrencyWithSymbol(item.retail_price || item.retailPrice || 0)}</td>
                                <td>${item.units || 0}</td>
                                <td>${formatCurrencyWithSymbol(itemRevenue)}</td>
                                <td>${formatCurrencyWithSymbol(itemExpenses)}</td>
                                <td class="${profitClass}">${formatCurrencyWithSymbol(itemProfit)}</td>
                            </tr>
                        `;
                    }).join('') : ''}
                    <tr class="total-row">
                        <td><strong>Totals</strong></td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td><strong>${totalUnits}</strong></td>
                        <td><strong>${formatCurrencyWithSymbol(data.revenue || 0)}</strong></td>
                        <td><strong>${formatCurrencyWithSymbol(data.expenses || 0)}</strong></td>
                        <td class="${data.netProfit >= 0 ? 'profit' : 'loss'}">
                            <strong>${formatCurrencyWithSymbol(data.netProfit || 0)}</strong>
                        </td>
                    </tr>
                </tbody>
            </table>

            <div class="summary">
                <p><strong>Net Profit:</strong>
                    <span class="${data.netProfit >= 0 ? 'profit' : 'loss'}">
                        ${formatCurrencyWithSymbol(data.netProfit)}
                    </span>
                </p>
                <p><strong>Profit Margin:</strong>
                    <span class="${data.netProfit >= 0 ? 'profit' : 'loss'}">
                        ${((data.netProfit / data.revenue) * 100).toFixed(2)}%
                    </span>
                </p>
            </div>
        </body>
        </html>
    `;

    doc.write(content);
    doc.close();

    // Print the iframe
    iframe.contentWindow.print();

    // Remove the iframe after printing
    setTimeout(() => {
        document.body.removeChild(iframe);
    }, 500);
};

export default ProfitLossReport;
